import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Redirect } from "react-router-dom/cjs/react-router-dom.min";
import { Box, Flex } from "rebass";
import { Avatar, OrderFilter } from "../components";
import MobileScrollCTA from "../components/MobileScrollCTA";
import { LoadingFrame } from "../components/common/LoadingFrame";
import { PreLoader } from "../components/loader/PreLoader";
import Order from "../components/order/Order";
import OrderStatusBadge from "../components/order/OrderStatusBadge";
import useGuardCustomerRoute from "../hooks/useGuardCustomerRoute";
import useResponsive from "../hooks/useResponsive";
import useVirtualList from "../hooks/useVirtualList";
import { MainLayout } from "../layouts/MainLayout";
import { yodaCustomerActions, yodaOrdersActions } from "../logic/actions";
import { ORDER_STATUS } from "../model/order";
import { getLocalizedString, theme } from "../utils";
import getOrderStatusCount from "../utils/getOrderStatusCount";
import config from "../utils/globalConfig";
import { resetScroll } from "../utils/resetScroll";

const OrdersPage = () => {
  const dispatch = useDispatch();
  const staticSectionRef = useRef();
  const [staticSectionHeight, setStaticSectionHeight] = useState(0);
  const { orders } = useSelector((state) => state.yodaOrders);
  const { me } = useSelector((state) => state.yodaStakeholders);
  const { customer } = useSelector((state) => state.yodaCustomer);
  const { offsetTop } = useSelector((state) => state.system);

  const { scrolledToBottom } = useSelector((state) => state.layout);
  const { customerId } = useParams();
  const [filters, setFilters] = useState({
    season: "-",
    typeId: "-",
    line: "-",
    status: "-",
  });
  const [ordersFiltered, setOrdersFiltered] = useState(orders);
  const [doIHaveMe, setDoIHaveMe] = useState(false);

  const { isMobile, isTabletSm } = useResponsive();
  const { getVirtualItems, reset } = useVirtualList({ enableAutoScroll: true });

  const displayedOrders = useMemo(
    () => getVirtualItems(ordersFiltered),
    [getVirtualItems, ordersFiltered],
  );

  useGuardCustomerRoute();

  const { draftCount, confirmedCount, cancelledCount, sentCount } =
    useMemo(() => {
      return getOrderStatusCount(orders);
    }, [orders]);

  useEffect(() => {
    setOrdersFiltered(orders);
  }, [orders]);

  useEffect(() => {
    let tmpOrders = orders
      .filter((order) => {
        if (filters.status === ORDER_STATUS.CANCELLED) {
          return (
            order?.recycled ||
            order?.status === ORDER_STATUS.CANCELLED ||
            (order.status === 0 && order.statusDescription === "Cancelled")
          );
        } else if (filters.status === ORDER_STATUS.DRAFT) {
          return (
            order.status === ORDER_STATUS.DRAFT &&
            order.statusDescription !== "Cancelled"
          );
        } else if (filters.status === ORDER_STATUS.SUBMITTED) {
          return (
            order.status === ORDER_STATUS.SUBMITTED &&
            order.statusDescription !== "Cancelled"
          );
        } else if (filters.status === ORDER_STATUS.CONFIRMED) {
          return (
            order.status === ORDER_STATUS.CONFIRMED &&
            order.statusDescription !== "Cancelled"
          );
        }
        return true;
      })
      .filter((order) =>
        filters.typeId !== "all" && filters.typeId !== "-"
          ? order.typeId === filters.typeId
          : true,
      )
      .filter((order) =>
        filters.season !== "all" && filters.season !== "-"
          ? order.seasonId === filters.season
          : true,
      )
      .filter((order) =>
        filters.line !== "all" && filters.line !== "-"
          ? order.customData?.lineId === filters.line
          : true,
      );

    setOrdersFiltered(tmpOrders);
    reset();
    resetScroll();
  }, [filters]);

  useEffect(() => {
    setStaticSectionHeight(staticSectionRef?.current?.clientHeight);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [staticSectionRef?.current?.clientHeight]);

  useEffect(() => {
    if (me?.role && !doIHaveMe) {
      setDoIHaveMe(true);
      dispatch(yodaOrdersActions.getOrderConfiguration(customerId));

      if (me.role === "CustomerCare") {
        dispatch(yodaCustomerActions.getOne(customerId));
        dispatch(yodaOrdersActions.getAllHeaders(customerId));
      }

      if (me.role === "Agents") {
        dispatch(yodaCustomerActions.getOne(customerId));
        dispatch(yodaOrdersActions.getAllHeaders(customerId));
      }

      if (me.role === "Customer") {
        dispatch(yodaCustomerActions.getOne(customerId));
        dispatch(yodaOrdersActions.getAllHeaders(customerId));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  function handleResize() {
    setStaticSectionHeight(staticSectionRef?.current?.clientHeight);
  }

  if (me?.error === "not-authorized") {
    return (
      <Redirect
        to={{
          pathname: "/not-authorized",
          state: {
            yodaError: true,
          },
        }}
      />
    );
  }
  if (customer?.channelCode === "2") {
    return (
      <Redirect
        to={{
          pathname: "/user-locked",
        }}
      />
    );
  }
  return (
    <MainLayout>
      <LoadingFrame order={true}></LoadingFrame>
      <Box
        sx={{
          position: "sticky",
          top: [offsetTop, 0],
          zIndex: 1,
          background: "white",
          width: ["100%", "calc(100% -32px)"],
          maxWidth: config.VIEWPORT,
          margin: "0 auto",
          padding: ["0 8px", 0],
        }}
        ref={staticSectionRef}
      >
        <Flex
          width={1}
          justifyContent={"space-between"}
          flexDirection={["column", "column", "column"]}
          mt={20}
          mb={30}
        >
          <Avatar
            name={customer && customer.name}
            size={40}
            nameSize="20px"
            isPage
            pl={[null, null, 0]}
            mb={["5px", null, null]}
          />
          <Flex
            style={{
              fontSize: "1em",
              marginLeft: !isMobile && !isTabletSm ? "55px" : null,
            }}
            flexDirection={["column", "column", "row"]}
            mb={[1, null, null, 0]}
          >
            {!isMobile && !isTabletSm && (
              <Flex p={1} ml={1} pb={1} alignItems={"center"}>
                {customer && customer.code} - {customer && customer.name}
                {getLocalizedString("Recap incipit")}
              </Flex>
            )}
            {(isMobile || isTabletSm) && (
              <OrderFilter
                setFilters={setFilters}
                filters={filters}
                orders={orders}
                lineFromCustomerConfig
              />
            )}
            {!isMobile && !isTabletSm && (
              <Flex
                sx={{
                  gap: "4px",
                  flexWrap: "wrap",
                  justifyContent: ["center", "center", "flex-start"],
                  paddingTop: ["10px", "20px", 0],
                }}
              >
                <OrderStatusBadge
                  color={theme.draftColor}
                  label={getLocalizedString(
                    draftCount === 1 ? "RECAP_DRAFT_SINGULAR" : "RECAP_DRAFT",
                  )}
                  count={draftCount}
                />
                <OrderStatusBadge
                  color={theme.confirmedColor}
                  label={getLocalizedString(
                    confirmedCount === 1
                      ? "Recap draft confirmed/singular"
                      : "Recap draft confirmed/plural",
                  )}
                  count={confirmedCount}
                />
                <OrderStatusBadge
                  color={theme.cancelledColor}
                  label={getLocalizedString(
                    cancelledCount === 1
                      ? "Recap draft cancelled/singular"
                      : "Recap draft cancelled/plural",
                  )}
                  count={cancelledCount}
                />
                <OrderStatusBadge
                  color={theme.waitingColor}
                  label={getLocalizedString(
                    sentCount === 1
                      ? "Recap draft waiting/singular"
                      : "Recap draft waiting/plural",
                  )}
                  count={sentCount}
                />
              </Flex>
            )}
          </Flex>
        </Flex>
        {!isMobile && !isTabletSm && (
          <OrderFilter
            setFilters={setFilters}
            filters={filters}
            lineFromCustomerConfig
          />
        )}
        <Box
          sx={{ height: [0, 0, 15] }}
          style={{ backgroundColor: "white" }}
        ></Box>
      </Box>
      <Box sx={{ width: "100%" }}>
        {displayedOrders?.map((order, index) => {
          return (
            <Order
              {...order}
              instance={order}
              key={`order-${index}-${order?.orderNumber}`}
            />
          );
        })}
        {!!displayedOrders?.length &&
          displayedOrders?.length < ordersFiltered?.length &&
          scrolledToBottom && (
            <Flex
              justifyContent={"center"}
              alignContent={"center"}
              sx={{ width: "100%", py: "20px" }}
            >
              <PreLoader color="black" size="sm" />
            </Flex>
          )}
        {(!displayedOrders || displayedOrders.length === 0) && (
          <Flex
            style={{
              height: "30px",
              display: "grid",
              justifyContent: "center",
              alignContent: "center",
            }}
            nt={"center"}
            mb={4}
            fontSize={12}
          >
            {getLocalizedString("ORDERS IS EMPTY")}
          </Flex>
        )}
        {(isMobile || isTabletSm) &&
          !!displayedOrders?.length &&
          displayedOrders?.length < ordersFiltered?.length && (
            <MobileScrollCTA />
          )}
      </Box>
    </MainLayout>
  );
};

export { OrdersPage };
